import type { AxiosResponse } from "axios";
import type { UserActivityPermissionPayload, UserFormPayload } from "src/services/userService";
import { getFromLocal } from "src/util/cache.service";
import { LOCAL_KEYS, USER_ROLE } from "src/util/util.constant";
import type {
  PermissionSelectValue,
  SelectOption,
  User,
  UserActivityPermission,
  UserDetailResponse,
  UserFormValues,
  UserNamedReference,
  UserRole,
  UserSystem,
  UserTimedDataPermission,
} from "./types";

export const CUSTOMER_USER_ROLE_ID = 5;
export const CUSTOMER_ROLE_IDS = new Set(["4", "5"]);

export const DEFAULT_LANGUAGE_ID = 162;
export const DEFAULT_LANGUAGE_NAME = "English";

export const getResponseArray = <T>(payload: unknown): T[] => {
  const data = (payload as AxiosResponse | undefined)?.data ?? payload;

  if (Array.isArray(data)) return data as T[];
  if (!data || typeof data !== "object") return [];

  const value = data as { response?: unknown; data?: unknown; items?: unknown };

  if (Array.isArray(value.response)) return value.response as T[];
  if (Array.isArray(value.data)) return value.data as T[];
  if (Array.isArray(value.items)) return value.items as T[];

  return [];
};

export const getResponseObject = <T>(payload: unknown): T | undefined => {
  const data = (payload as AxiosResponse | undefined)?.data ?? payload;

  if (!data || typeof data !== "object") return undefined;

  const value = data as { response?: unknown };

  if (value.response && typeof value.response === "object") {
    return value.response as T;
  }

  return data as T;
};

export const normalizeUserDetailResponse = (payload: unknown): UserDetailResponse | undefined => {
  const response = getResponseObject<UserDetailResponse | User>(payload);

  if (!response || typeof response !== "object") return undefined;

  if ("user" in response && response.user) {
    return response as UserDetailResponse;
  }

  if ("id" in response || "userId" in response) {
    return { user: response as User };
  }

  return undefined;
};

export const toSelectOptions = (
  items: Array<UserNamedReference | UserRole | UserTimedDataPermission>
): SelectOption[] =>
  items
    .map((item) => {
      const source = item as Record<string, unknown>;
      const value = source.id ?? source.settingId;
      const label = source.name ?? source.companyName ?? source.code ?? value ?? "--";

      return {
        label: String(label),
        value: typeof value === "string" || typeof value === "number" ? value : "",
      };
    })
    .filter((option) => option.value !== "");

export const getCurrentRole = () => {
  const directRole = getFromLocal<string>(LOCAL_KEYS.ROLE, false, true);
  const userDetail = getFromLocal<{ role?: string; userRole?: { name?: string } }>(
    LOCAL_KEYS.USER_DETAIL,
    true,
    true
  );

  return directRole ?? userDetail?.role ?? userDetail?.userRole?.name ?? "";
};

export const isHighLevelRole = (role: string) =>
  (
    [USER_ROLE.SuperUser, USER_ROLE.RootrUser, USER_ROLE.CRMAdmin, USER_ROLE.CRMUser] as string[]
  ).includes(role);

export const isCustomerRoleValue = (roleId?: string | number, roleName?: string) =>
  (roleId !== undefined && CUSTOMER_ROLE_IDS.has(String(roleId))) ||
  roleName === "Customer Admin" ||
  roleName === "Customer User" ||
  roleName === USER_ROLE.CustomerAdmin ||
  roleName === USER_ROLE.CustomerUser;

export const isCustomerUserRoleValue = (roleId?: string | number, roleName?: string) =>
  String(roleId) === String(CUSTOMER_USER_ROLE_ID) ||
  roleName === "Customer User" ||
  roleName === USER_ROLE.CustomerUser;

export const isSuperUserRoleValue = (roleId?: string | number, roleName?: string) =>
  String(roleId) === "2" ||
  Boolean(roleName?.replace(/\s+/g, "").toLowerCase() === USER_ROLE.SuperUser.toLowerCase());

export const isSuperAdminRoleValue = (roleName?: string) =>
  roleName?.replace(/\s+/g, "").toLowerCase() === "superadmin";

export const getRoleName = (roles: UserRole[], roleId?: string | number) =>
  roles.find((role) => String(role.id) === String(roleId))?.name;

export const getUserId = (user?: User | null) => user?.id ?? user?.userId;

const getRecordValue = (record: User | null | undefined, keys: string[]) => {
  if (!record) return undefined;

  const source = record as Record<string, unknown>;

  for (const key of keys) {
    const value = source[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
};

const getReferenceId = (
  record: User | null | undefined,
  referenceKeys: string | string[],
  fallbackKeys: string[]
) => {
  const reference = getRecordValue(
    record,
    Array.isArray(referenceKeys) ? referenceKeys : [referenceKeys]
  );

  if (reference && typeof reference === "object") {
    const referenceId = (reference as UserNamedReference).id;
    if (referenceId !== undefined && referenceId !== null) return referenceId;
  }

  const directValue = getRecordValue(record, fallbackKeys);

  return typeof directValue === "string" || typeof directValue === "number"
    ? directValue
    : undefined;
};

export const buildInitialFormValues = (record?: User | null): UserFormValues => ({
  username: record?.username ?? "",
  name: record?.name ?? "",
  surname: record?.surname ?? "",
  languageId: getReferenceId(record, "language", ["languageId"]) ?? DEFAULT_LANGUAGE_ID,
  email: record?.email ?? "",
  retypeEmail: record?.email ?? "",
  userRoleId: getReferenceId(record, "userRole", ["userRoleId", "roleId"]),
  distributorId: getReferenceId(record, "distributor", ["distributorId"]),
  clientId: getReferenceId(record, ["client", "customer"], ["clientId", "customerId"]),
});

export const shouldRequirePassword = (isEditing: boolean, roleId?: string | number) =>
  !isEditing && String(roleId) !== String(CUSTOMER_USER_ROLE_ID);

export const buildUserPayload = (
  values: UserFormValues,
  fallbackDistributorId?: string | number | null,
  options: { includePassword?: boolean; omitDistributor?: boolean } = {}
): UserFormPayload => {
  const payload: UserFormPayload = {
    name: values.name.trim(),
    surname: values.surname.trim(),
    username: values.username.trim(),
    email: values.email.trim(),
    clientId: values.clientId,
    userRoleId: values.userRoleId,
    languageId: values.languageId,
    distributorId:
      options.omitDistributor || String(values.userRoleId) === "2"
        ? undefined
        : (values.distributorId ?? fallbackDistributorId ?? undefined),
  };

  if (options.includePassword && values.password?.trim()) {
    payload.password = values.password.trim();
  }

  return payload;
};

export const getSystemName = (system: UserSystem) =>
  system.name === "FDMR" ? "FDMr" : system.name;

export const shouldShowSystemForRole = (
  system: UserSystem,
  roleId?: string | number,
  roleName?: string
) => {
  if (!isCustomerRoleValue(roleId, roleName)) return true;
  return system.name.toLowerCase() !== "crm";
};

const matchesRoleName = (roleName: string | undefined, names: string[]) => {
  if (!roleName) return false;
  const normalizedRoleName = roleName.replace(/\s+/g, "").toLowerCase();

  return names.some((name) => normalizedRoleName === name.replace(/\s+/g, "").toLowerCase());
};

export const shouldShowPermissionSettingsForRole = (
  system: UserSystem,
  roleId?: string | number,
  roleName?: string
) => {
  const systemName = system.name.toLowerCase();

  if (systemName === "crm") {
    if (isCustomerRoleValue(roleId, roleName)) return false;
    return matchesRoleName(roleName, ["CRM User", USER_ROLE.CRMUser]);
  }

  if (systemName === "fdmr" || systemName === "tpms") {
    return isCustomerUserRoleValue(roleId, roleName);
  }

  return true;
};

export const isCrmSystem = (system?: UserSystem) => system?.name.toLowerCase() === "crm";

const isPermissionFlagEnabled = (value: unknown) => {
  if (value === true || value === 1) return true;
  if (typeof value !== "string") return false;

  return ["true", "1"].includes(value.trim().toLowerCase());
};

const getPermissionFlag = (
  permission: UserActivityPermission,
  primaryKey: string,
  fallbackKey?: string
) => {
  const source = permission as Record<string, unknown>;
  const value = source[primaryKey] ?? (fallbackKey ? source[fallbackKey] : undefined);

  return isPermissionFlagEnabled(value);
};

export const permissionToSelectValue = (
  permission: UserActivityPermission,
  options: { showUpselling?: boolean } = {}
): PermissionSelectValue[] => {
  const values: PermissionSelectValue[] = [];
  const enabled = getPermissionFlag(permission, "enabled");
  const canView = getPermissionFlag(permission, "canView", "view");
  const canEdit = getPermissionFlag(permission, "canEdit", "edit");
  const canDelete = getPermissionFlag(permission, "canDelete", "delete");
  const isUpselling = options.showUpselling && canView && !enabled;

  if (enabled) values.push("enabled");
  if (isUpselling) {
    values.push("upselling");
  } else if (canView) {
    values.push("canView");
  }
  if (canEdit) values.push("canEdit");
  if (canDelete) values.push("canDelete");
  if (enabled && canView && canEdit && canDelete) {
    values.push("all");
  }

  return values;
};

export const normalizeUserActivityPermission = (
  permission: UserActivityPermission
): UserActivityPermission => ({
  ...permission,
  enabled: getPermissionFlag(permission, "enabled"),
  canView: getPermissionFlag(permission, "canView", "view"),
  canEdit: getPermissionFlag(permission, "canEdit", "edit"),
  canDelete: getPermissionFlag(permission, "canDelete", "delete"),
});

export const selectValueToPermission = (
  permission: UserActivityPermission,
  value: PermissionSelectValue,
  checked: boolean
): UserActivityPermission => {
  if (value === "upselling") {
    return {
      ...permission,
      enabled: false,
      canView: checked,
      canEdit: false,
      canDelete: false,
      upselling: checked,
      permission: undefined,
    };
  }

  if (value === "all") {
    return {
      ...permission,
      enabled: checked,
      canView: checked,
      canEdit: checked,
      canDelete: checked,
      upselling: false,
      permission: undefined,
    };
  }

  return {
    ...permission,
    [value]: checked,
    upselling: false,
    permission: undefined,
  };
};

export const buildUserActivitiesPayload = (
  permissions: UserActivityPermission[],
  userId: string | number
): UserActivityPermissionPayload[] =>
  permissions.map((permission) => {
    const normalizedPermission = normalizeUserActivityPermission(permission);

    return {
      userId,
      activityId: permission.activityId,
      enabled: normalizedPermission.enabled ?? false,
      canView: normalizedPermission.canView ?? false,
      canEdit: normalizedPermission.canEdit ?? false,
      canDelete: normalizedPermission.canDelete ?? false,
    };
  });
